import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { currentUserKey } from './api/auth'
import { ApiError } from './api/client'
import { App } from './App'
import { ConfirmProvider } from './components/ConfirmDialog'
import { ToastProvider } from './components/Toast'
import './styles.css'

/** Un 401 en CUALQUIER consulta significa que la sesión se murió, y el
 *  usuario en cache deja de ser cierto en ese mismo instante.
 *
 *  Sin esto, `useCurrentUser` se sigue creyendo su copia cacheada durante su
 *  `staleTime` y la app queda en un estado que miente: el cascarón dibujado
 *  con tu email arriba y, adentro, "No autenticado". Vaciar la clave del
 *  usuario hace que `App` mande al login, que es lo que de verdad pasó.
 */
function onUnauthorized(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    queryClient.setQueryData(currentUserKey, null)
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onUnauthorized }),
  mutationCache: new MutationCache({ onError: onUnauthorized }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('No se encontró el elemento #root')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)

/** Sólo en build de producción — nunca en `vite dev`: un service worker
 *  cacheando los módulos del propio servidor de desarrollo rompería el
 *  hot-reload (serviría versiones viejas de JS aunque el archivo ya haya
 *  cambiado en disco). `updateViaCache: 'none'` evita que el navegador
 *  cachee `sw.js` por su cuenta hasta 24h — con deploys frecuentes,
 *  cada visita tiene que poder ver una versión nueva del propio worker.
 *  `controllerchange` recarga una sola vez cuando ese worker nuevo toma
 *  control, para no dejar a nadie mirando el shell viejo en silencio. */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  let reloading = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  })
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
  })
}
